"use strict";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { END_TO_END_AUTOMATION } from "../../ApiEndpoints";
import { postWithFirebaseAuth } from "../../utils/firebaseApiClient";
import ModeCards from "./ModeCards.js";
import { DEFAULT_STEM_SEPARATION_PROVIDER } from "./modeOptions.js";
import {
    buildReloadVideoRequest,
    isValidVideoUrl,
    RELOAD_VIDEO_RUN_OPTIONS,
    RELOAD_VIDEO_SOURCES,
} from "./reloadVideo.js";
import { uploadProjectVideo } from "./uploadProjectVideo.js";

const labelSx = {
    fontFamily: "var(--font-body)",
    fontSize: 13,
    fontWeight: 600,
    color: "text.secondary",
    mb: 0.75,
};

const Field = ({ label, children }) => (
    <Box>
        <Box sx={labelSx}>{label}</Box>
        {children}
    </Box>
);

/**
 * Loads a new video into a project that has no dialogue lines yet and starts
 * the pipeline again from ingestion, as project creation does.
 *
 * stemSeparationOptions: [{ value, label }]
 */
const ReloadVideoDialog = ({
    open,
    onClose,
    onStarted,
    projectId,
    projectName,
    hasVideo,
    stemSeparationOptions,
    defaultStemSeparationProvider = DEFAULT_STEM_SEPARATION_PROVIDER,
}) => {
    const [source, setSource] = useState("file");
    const [file, setFile] = useState(null);
    const [driveUrl, setDriveUrl] = useState("");
    const [executionMode, setExecutionMode] = useState("full");
    const [provider, setProvider] = useState(defaultStemSeparationProvider);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Each opening starts from the defaults project creation uses.
    useEffect(() => {
        if (!open) {
            return;
        }
        setSource("file");
        setFile(null);
        setDriveUrl("");
        setExecutionMode("full");
        setProvider(defaultStemSeparationProvider);
        setUploadProgress(null);
        setError(null);
    }, [open, defaultStemSeparationProvider]);

    const hasSource = source === "file" ? Boolean(file) : isValidVideoUrl(driveUrl);
    const showUrlError = source === "google-drive" && driveUrl.trim() !== "" && !hasSource;

    const handleClose = () => {
        if (!submitting) {
            onClose();
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);

        try {
            const url =
                source === "file"
                    ? await uploadProjectVideo({
                        file,
                        projectName,
                        onProgress: setUploadProgress,
                    })
                    : driveUrl.trim();

            const response = await postWithFirebaseAuth(
                END_TO_END_AUTOMATION,
                buildReloadVideoRequest({
                    projectId,
                    projectName,
                    url,
                    executionMode,
                    stemSeparationProvider: provider,
                })
            );
            // runId lets the page show the video as loading until it is ready.
            onStarted?.({ executionMode, runId: response?.data?.runId || null });
        } catch (err) {
            console.error("Error reloading the project video:", err);
            setError(
                err?.response?.data?.message ||
                "The video could not be loaded. Check the file or link and try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const submitLabel = submitting
        ? uploadProgress !== null && uploadProgress < 100
            ? "Uploading…"
            : "Starting…"
        : hasVideo
            ? "Replace video"
            : "Load video";

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{hasVideo ? "Reload video" : "Upload video"}</DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                    {hasVideo && (
                        <Alert severity="warning">
                            This replaces the project&apos;s video. Stem separation runs again
                            on the new one.
                        </Alert>
                    )}

                    <Field label="Video source">
                        <ModeCards
                            value={source}
                            onChange={setSource}
                            options={RELOAD_VIDEO_SOURCES}
                            ariaLabel="Video source"
                            disabled={submitting}
                        />
                    </Field>

                    {source === "file" ? (
                        <Field label="Video file">
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={submitting}
                                sx={{ textTransform: "none" }}
                            >
                                {file ? "Choose another file" : "Choose a file"}
                                <input
                                    hidden
                                    type="file"
                                    accept="video/*"
                                    aria-label="Video file"
                                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                                />
                            </Button>
                            {file && (
                                <Box
                                    component="span"
                                    sx={{ ml: 1.5, fontSize: 14, wordBreak: "break-all" }}
                                >
                                    {file.name}
                                </Box>
                            )}
                        </Field>
                    ) : (
                        // A label above the field: the app's global input styles
                        // collide with MUI's floating label.
                        <Field label="Google Drive link">
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="https://drive.google.com/file/d/…"
                                value={driveUrl}
                                onChange={(event) => setDriveUrl(event.target.value)}
                                disabled={submitting}
                                error={showUrlError}
                                inputProps={{ "aria-label": "Google Drive link" }}
                                helperText={
                                    showUrlError
                                        ? "Paste the full link, starting with https://"
                                        : "A link to the video file, not to a folder"
                                }
                            />
                        </Field>
                    )}

                    <Field label="Stem separation">
                        <Select
                            size="small"
                            value={provider}
                            onChange={(event) => setProvider(event.target.value)}
                            disabled={submitting}
                            inputProps={{ "aria-label": "Stem separation service" }}
                        >
                            {stemSeparationOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Field>

                    <Field label="After the video loads">
                        <ModeCards
                            value={executionMode}
                            onChange={setExecutionMode}
                            options={RELOAD_VIDEO_RUN_OPTIONS}
                            ariaLabel="What runs after the video loads"
                            disabled={submitting}
                        />
                    </Field>

                    {submitting && source === "file" && uploadProgress !== null && (
                        <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            aria-label="Upload progress"
                        />
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={handleSubmit}
                    disabled={!hasSource || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReloadVideoDialog;
